import { useMsal } from '@azure/msal-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useContext, useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import {
    FaCheckCircle,
    FaExclamationCircle,
    FaExclamationTriangle,
    FaPause,
    FaPlay,
    FaSyncAlt,
    FaUnlink,
    FaUserCheck
} from 'react-icons/fa';
import { UserModel } from '../../_core/_model.ts';
import { AuthContext } from '../../context/AuthContext.js';
import { ThemeContext } from '../../context/ThemeContext.js';
import { UpsertUser } from '../Forms/UserRequest/_core/_request.ts';

interface SyncAdUsersModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    localAdUsers: UserModel[];
}

type SyncStatus = 'idle' | 'syncing' | 'success' | 'error' | 'unlinked';

interface UserSyncState {
    status: SyncStatus;
    errorMsg?: string;
}

const blobToBase64 = (blob: Blob): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

const CHUNK_SIZE = 5; // Process 5 users concurrently to balance speed and avoid MS Graph Rate Limits (429)

export default function SyncAdUsersModal({ isOpen, onClose, onSuccess, localAdUsers }: SyncAdUsersModalProps) {
    const { user: loggedInUser } = useContext(AuthContext);
    const { themeConfig } = useContext(ThemeContext);
    const { instance, accounts } = useMsal();

    // -- State --
    const [phase, setPhase] = useState<'selection' | 'running' | 'completed'>('selection');
    const [isPaused, setIsPaused] = useState(false);
    const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
    const [adFilter, setAdFilter] = useState<'all' | number>(0);

    const [userStatuses, setUserStatuses] = useState<Record<number, UserSyncState>>({});
    const [currentIndex, setCurrentIndex] = useState(0);
    const [results, setResults] = useState({ success: 0, failed: 0 });

    const isPausedRef = useRef(isPaused);
    const stopRequestedRef = useRef(false);

    useEffect(() => {
        isPausedRef.current = isPaused;
    }, [isPaused]);

    // Safely compute filtered users, handling nulls or stringified numbers from the API
    const filteredAdUsers = localAdUsers.filter(u => {
        if (adFilter === 'all') return true;
        const status = u.isAdUser != null ? Number(u.isAdUser) : 0; // Default undefined/null to 0 (Local)
        return status === adFilter;
    });

    useEffect(() => {
        if (isOpen) {
            setPhase('selection');
            setIsPaused(false);
            stopRequestedRef.current = false;
            setCurrentIndex(0);
            setResults({ success: 0, failed: 0 });
            setAdFilter(0); // Reset filter back to Local Users on open

            // Initialize statuses for all users
            const initialStatuses: Record<number, UserSyncState> = {};
            localAdUsers.forEach(u => {
                initialStatuses[u.userID] = { status: 'idle' };
            });
            setUserStatuses(initialStatuses);

            // Select ONLY the users that match the default filter (Imported/0) initially
            const initialFiltered = localAdUsers.filter(u => {
                const status = u.isAdUser != null ? Number(u.isAdUser) : 0;
                return status === 0;
            });
            setSelectedUsers(new Set(initialFiltered.map(u => u.userID)));
        }
        // We INTENTIONALLY omit localAdUsers from the dependency array here.
        // This prevents the user's selections from randomly resetting if the parent 
        // component re-renders or updates the localAdUsers array in the background.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const handleToggleSelectAll = (checked: boolean) => {
        const newSet = new Set(selectedUsers);
        if (checked) {
            // Add all currently filtered users to the selection
            filteredAdUsers.forEach(u => newSet.add(u.userID));
        } else {
            // Remove all currently filtered users from the selection
            filteredAdUsers.forEach(u => newSet.delete(u.userID));
        }
        setSelectedUsers(newSet);
    };

    const handleToggleUser = (userId: number, checked: boolean) => {
        const newSet = new Set(selectedUsers);
        if (checked) newSet.add(userId);
        else newSet.delete(userId);
        setSelectedUsers(newSet);
    };

    const fetchUserProfilePicture = async (userId: string, accessToken: string) => {
        try {
            const response = await fetch(`https://graph.microsoft.com/v1.0/users/${userId}/photo/$value`, {
                headers: { "Authorization": `Bearer ${accessToken}` }
            });
            if (response.ok) {
                const blob = await response.blob();
                return await blobToBase64(blob);
            }
        } catch (e) {
            // Photos often error out if user has no photo set, safe to ignore
        }
        return null;
    };

    const processUser = async (localUser: UserModel, accessToken: string) => {
        try {
            const email = localUser.email;
            if (!email) throw new Error("Local user missing email address.");

            // Safely escape single quotes for OData query to prevent AD fetch failures
            const safeEmail = email.replace(/'/g, "''");
            const filterQuery = `mail eq '${safeEmail}' or userPrincipalName eq '${safeEmail}'`;
            const selectParams = "id,displayName,givenName,surname,mail,userPrincipalName,jobTitle,department,country,city,officeLocation,mobilePhone,accountEnabled,userType,companyName,employeeType,onPremisesExtensionAttributes";
            const endpoint = `https://graph.microsoft.com/v1.0/users?$filter=${filterQuery}&$select=${selectParams}&$expand=manager($select=displayName,mail,userPrincipalName)&$count=true`;

            const graphResponse = await fetch(endpoint, {
                method: 'GET',
                headers: {
                    "Authorization": `Bearer ${accessToken}`,
                    "ConsistencyLevel": "eventual"
                }
            });

            if (graphResponse.status === 429) throw new Error("Throttled by Microsoft Graph (429).");
            if (!graphResponse.ok) throw new Error(`AD fetch failed: ${graphResponse.status}`);

            const graphData = await graphResponse.json();

            // Logic for users not found in AD
            if (!graphData.value || graphData.value.length === 0) {
                const unlinkedRequest: any = {
                    ...localUser,
                    isAdUser: -1, // Mark as unlinked
                    byUser: loggedInUser?.userID || 1
                };

                const response = await UpsertUser(unlinkedRequest);
                if (response?.data && typeof response.data === 'string' && response.data.trim() !== '') {
                    throw new Error(response.data);
                }

                return { id: localUser.userID, status: 'unlinked' };
            }

            const adUser = graphData.value[0];
            const photoBase64 = await fetchUserProfilePicture(adUser.id, accessToken);

            const nameParts = (adUser.displayName || "").split(" ");
            const firstName = adUser.givenName || nameParts[0] || localUser.userFirstName;
            const lastName = adUser.surname || nameParts.slice(1).join(" ") || localUser.userLastName;

            const requestData: any = {
                ...localUser,
                userID: localUser.userID,
                userName: `${firstName} ${lastName}`.trim(),
                userFirstName: firstName,
                userLastName: lastName,
                email: adUser.mail || adUser.userPrincipalName,
                profilePicture: photoBase64,
                status: adUser.accountEnabled !== false ? "active" : "inactive",
                isAdUser: 1,
                jobTitle: adUser.jobTitle,
                department: adUser.onPremisesExtensionAttributes?.extensionAttribute6 || adUser.department,
                country: adUser.country,
                city: adUser.city,
                officeLocation: adUser.officeLocation,
                mobilePhone: adUser.mobilePhone,
                accountEnabled: adUser.accountEnabled,
                companyName: adUser.companyName,
                employeeType: adUser.employeeType,
                manager: adUser.manager,
                userPrincipalName: adUser.userPrincipalName,
                adUserId: adUser.id,
                byUser: loggedInUser?.userID || 1
            };

            const response = await UpsertUser(requestData);
            if (response?.data && typeof response.data === 'string' && response.data.trim() !== '') {
                throw new Error(response.data);
            }

            return { id: localUser.userID, status: 'success' };
        } catch (err: any) {
            return { id: localUser.userID, status: 'error', errorMsg: err.message || 'Sync failed' };
        }
    };

    const runSyncLoop = async (startIndex: number) => {
        if (accounts.length === 0) return;

        const targetUsers = localAdUsers.filter(u => selectedUsers.has(u.userID));
        let currentSuccess = results.success;
        let currentFailed = results.failed;

        for (let i = startIndex; i < targetUsers.length; i += CHUNK_SIZE) {
            if (isPausedRef.current) {
                setCurrentIndex(i);
                return;
            }
            if (stopRequestedRef.current) return;

            setCurrentIndex(i);
            const chunk = targetUsers.slice(i, i + CHUNK_SIZE);

            // Pre-emptively update statuses to syncing for the entire chunk
            setUserStatuses(prev => {
                const next = { ...prev };
                chunk.forEach(u => { next[u.userID] = { status: 'syncing' }; });
                return next;
            });

            try {
                // Fetch token dynamically inside loop to avoid expiration on massive lists
                const tokenResponse = await instance.acquireTokenSilent({
                    scopes: ["User.Read.All"],
                    account: accounts[0]
                });
                const accessToken = tokenResponse.accessToken;

                // Process entire chunk simultaneously 
                const promises = chunk.map(u => processUser(u, accessToken));
                const chunkResults = await Promise.all(promises);

                let chunkSuccessCount = 0;
                let chunkFailedCount = 0;

                // Calculate success and failure counts BEFORE setting the state
                chunkResults.forEach(res => {
                    if (res.status === 'success' || res.status === 'unlinked') {
                        chunkSuccessCount++;
                    } else {
                        chunkFailedCount++;
                    }
                });

                // Batch UI updates at the end of the chunk to prevent freezing
                setUserStatuses(prev => {
                    const next = { ...prev };
                    chunkResults.forEach(res => {
                        next[res.id] = { status: res.status as SyncStatus, errorMsg: res.errorMsg };
                    });
                    return next;
                });

                // Update the tracked metrics accurately
                currentSuccess += chunkSuccessCount;
                currentFailed += chunkFailedCount;
                setResults({ success: currentSuccess, failed: currentFailed });

                // Brief pause to allow UI repainting & to mitigate MS Graph 429 Throttle Limits
                if (i + CHUNK_SIZE < targetUsers.length) {
                    await new Promise(res => setTimeout(res, 250));
                }

            } catch (error: any) {
                console.error("Critical Auth/Network Error:", error);
                setIsPaused(true);
                alert("Critical Error: Unable to communicate with Active Directory.");
                setCurrentIndex(i);
                return;
            }
        }

        if (!isPausedRef.current && !stopRequestedRef.current) {
            setPhase('completed');
            if (currentSuccess > 0) onSuccess();
        }
    };

    const handleStart = () => {
        setPhase('running');
        setIsPaused(false);
        runSyncLoop(0);
    };

    const handlePauseResume = () => {
        if (isPaused) {
            setIsPaused(false);
            runSyncLoop(currentIndex);
        } else {
            setIsPaused(true);
        }
    };

    const handleModalClose = () => {
        stopRequestedRef.current = true;
        if (results.success > 0 && phase !== 'completed') onSuccess();
        onClose();
    };

    if (!isOpen) return null;

    const targetUsers = localAdUsers.filter(u => selectedUsers.has(u.userID));
    const totalToSync = targetUsers.length;
    const isAllSelected = filteredAdUsers.length > 0 && filteredAdUsers.every(u => selectedUsers.has(u.userID));

    const processedCount = results.success + results.failed;
    const progressPercent = totalToSync === 0 ? 0 : Math.round((processedCount / totalToSync) * 100);

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1060, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(3px)' }}
            >
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="card shadow-lg d-flex flex-column overflow-hidden"
                    style={{
                        width: '95%', maxWidth: '800px', height: '80vh',
                        backgroundColor: themeConfig?.colors?.card || 'var(--bs-body-bg)',
                        color: themeConfig?.colors?.text || 'var(--bs-body-color)',
                        borderRadius: '12px'
                    }}
                >
                    <div className="card-header d-flex justify-content-between align-items-center py-3 border-bottom" style={{ backgroundColor: 'transparent' }}>
                        <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
                            <FaSyncAlt className="text-primary" /> Sync Active Directory Profiles
                        </h5>
                        <button className="btn-close shadow-none" onClick={handleModalClose}></button>
                    </div>

                    {phase !== 'selection' && (
                        <div className="p-4 border-bottom bg-primary-subtle text-primary">
                            <div className="d-flex justify-content-between align-items-end mb-2">
                                <div>
                                    <h5 className="fw-bold mb-1">
                                        {phase === 'completed' ? 'Synchronization Complete' :
                                            isPaused ? 'Synchronization Paused' : 'Synchronizing Data...'}
                                    </h5>
                                    <span className="small opacity-75 fw-semibold">
                                        Processed {processedCount} of {totalToSync} users ({results.failed} errors)
                                    </span>
                                </div>
                                <h3 className="mb-0 fw-bold">{progressPercent}%</h3>
                            </div>
                            <div className="progress bg-white bg-opacity-50" style={{ height: '8px', borderRadius: '4px' }}>
                                <motion.div
                                    className={`progress-bar ${phase === 'completed' ? (results.failed > 0 ? 'bg-warning' : 'bg-success') : 'bg-primary'}`}
                                    initial={{ width: 0 }}
                                    animate={{ width: `${progressPercent}%` }}
                                    transition={{ duration: 0.3 }}
                                    style={{
                                        backgroundImage: phase === 'running' && !isPaused ? 'linear-gradient(45deg,rgba(255,255,255,.15) 25%,transparent 25%,transparent 50%,rgba(255,255,255,.15) 50%,rgba(255,255,255,.15) 75%,transparent 75%,transparent)' : 'none',
                                        backgroundSize: '1rem 1rem'
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    <div className="card-body p-0 d-flex flex-column" style={{ overflow: 'hidden' }}>

                        {phase === 'selection' && (
                            <div className="px-4 py-3 border-bottom bg-body-tertiary d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3">
                                <p className="mb-0 small text-muted flex-grow-1">
                                    Select the users below you wish to pull fresh data for from Azure Active Directory. Users no longer found in AD will be marked as unlinked.
                                </p>
                                <div style={{ minWidth: '220px' }}>
                                    <select
                                        className="form-select form-select-sm shadow-none cursor-pointer border-secondary"
                                        value={adFilter}
                                        onChange={(e) => {
                                            setAdFilter(e.target.value === 'all' ? 'all' : Number(e.target.value)); 
                                            setSelectedUsers(new Set());
                                        }}
                                    >
                                        <option value="all">All Users</option>
                                        {/* <option value={0}>Local User (Imported)</option> */}
                                        <option value={1}>Active AD User</option>
                                        <option value={-1}>Unlinked AD User</option>
                                    </select>
                                </div>
                            </div>
                        )}

                        <div className="flex-grow-1 overflow-auto p-0">
                            <table className="table table-hover mb-0 align-middle">
                                <thead className="sticky-top bg-body-tertiary shadow-sm" style={{ zIndex: 5 }}>
                                    <tr>
                                        {phase === 'selection' && (
                                            <th className="border-0" style={{ width: '50px', paddingLeft: '24px' }}>
                                                <input type="checkbox" className="form-check-input shadow-none cursor-pointer"
                                                    checked={isAllSelected}
                                                    onChange={(e) => handleToggleSelectAll(e.target.checked)} />
                                            </th>
                                        )}
                                        <th className="border-0 text-muted small fw-bold" style={{ paddingLeft: phase !== 'selection' ? '24px' : '12px' }}>
                                            USER IDENTITY {phase === 'selection' && <span className="ms-1 fw-normal text-secondary">({filteredAdUsers.length} in view)</span>}
                                        </th>
                                        <th className="border-0 text-muted small fw-bold text-end" style={{ paddingRight: '24px' }}>SYNC STATUS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(phase === 'selection' ? filteredAdUsers : targetUsers).length === 0 ? (
                                        <tr>
                                            <td colSpan={3} className="text-center py-5 text-muted">
                                                <FaExclamationTriangle size={30} className="mb-3 opacity-50" />
                                                <h6>No users found for the selected filter.</h6>
                                            </td>
                                        </tr>
                                    ) : (
                                        (phase === 'selection' ? filteredAdUsers : targetUsers).map((u, idx) => {
                                            const state = userStatuses[u.userID];
                                            const isSelected = selectedUsers.has(u.userID);
                                            // Determine if this user falls inside the currently running batch chunk
                                            const isCurrentChunk = phase === 'running' && idx >= currentIndex && idx < currentIndex + CHUNK_SIZE;

                                            return (
                                                <tr
                                                    key={u.userID}
                                                    className={isCurrentChunk ? 'bg-primary-subtle' : ''}
                                                    style={{ opacity: (phase === 'selection' && !isSelected) ? 0.6 : 1, transition: 'background-color 0.3s ease' }}
                                                >
                                                    {phase === 'selection' && (
                                                        <td style={{ paddingLeft: '24px' }}>
                                                            <input type="checkbox" className="form-check-input shadow-none cursor-pointer"
                                                                checked={isSelected}
                                                                onChange={(e) => handleToggleUser(u.userID, e.target.checked)} />
                                                        </td>
                                                    )}

                                                    <td style={{ paddingLeft: phase !== 'selection' ? '24px' : '12px', padding: '12px' }}>
                                                        <div className="d-flex align-items-center gap-3">
                                                            <div className="bg-secondary bg-opacity-10 text-secondary d-flex align-items-center justify-content-center rounded-circle fw-bold flex-shrink-0"
                                                                style={{ width: '38px', height: '38px' }}>
                                                                {u.userName?.charAt(0) || '?'}
                                                            </div>
                                                            <div className="overflow-hidden">
                                                                <div className="fw-bold text-truncate">{u.userName}</div>
                                                                <small className="text-muted text-truncate d-block">{u.email}</small>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    <td className="text-end" style={{ paddingRight: '24px' }}>
                                                        {phase === 'selection' ? (
                                                            <span className="badge border border-secondary text-secondary fw-normal">Queued</span>
                                                        ) : (
                                                            <div className="d-flex flex-column align-items-end justify-content-center">
                                                                {state?.status === 'idle' && <span className="text-muted small"><FaPause size={10} className="me-1" /> Pending</span>}
                                                                {state?.status === 'syncing' && <span className="text-primary fw-bold small"><Spinner size="sm" animation="border" className="me-2" style={{ borderWidth: '2px', width: '12px', height: '12px' }} /> Syncing...</span>}
                                                                {state?.status === 'success' && <span className="text-success fw-bold small"><FaCheckCircle size={14} className="me-1" /> Updated</span>}

                                                                {/* Unlinked UI State */}
                                                                {state?.status === 'unlinked' && <span className="text-warning fw-bold small d-flex align-items-center gap-1"><FaUnlink size={13} /> Unlinked</span>}

                                                                {state?.status === 'error' && (
                                                                    <>
                                                                        <span className="text-danger fw-bold small"><FaExclamationCircle size={14} className="me-1" /> Failed</span>
                                                                        <small className="text-danger" style={{ fontSize: '10px', maxWidth: '200px' }}>{state.errorMsg}</small>
                                                                    </>
                                                                )}
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div className="card-footer p-3 border-top d-flex justify-content-between align-items-center bg-body-tertiary">
                        <div>
                            {phase === 'selection' ? (
                                <div className="d-flex flex-column">
                                    <span className="fw-bold small">{selectedUsers.size} Total Selected</span>
                                    {adFilter !== 'all' && (
                                        <span className="text-muted" style={{ fontSize: '11px' }}>
                                            {filteredAdUsers.filter(u => selectedUsers.has(u.userID)).length} selected in current view
                                        </span>
                                    )}
                                </div>
                            ) : (
                                <span className={`badge ${results.failed > 0 ? 'bg-warning text-dark' : 'bg-success'} px-3 py-2 shadow-sm`}>
                                    {results.success} Successful | {results.failed} Failed
                                </span>
                            )}
                        </div>

                        <div className="d-flex gap-2">
                            {phase === 'selection' && (
                                <>
                                    <button className="btn btn-light shadow-sm fw-semibold" onClick={handleModalClose}>Cancel</button>
                                    <button className="btn btn-primary px-4 shadow-sm fw-bold d-flex align-items-center gap-2"
                                        onClick={handleStart} disabled={selectedUsers.size === 0}>
                                        <FaUserCheck /> Start Sync ({selectedUsers.size})
                                    </button>
                                </>
                            )}

                            {phase === 'running' && (
                                <>
                                    <button className={`btn shadow-sm fw-bold d-flex align-items-center gap-2 ${isPaused ? 'btn-success' : 'btn-warning text-dark'}`}
                                        onClick={handlePauseResume}>
                                        {isPaused ? <><FaPlay /> Resume Sync</> : <><FaPause /> Pause Sync</>}
                                    </button>
                                    <button className="btn btn-outline-secondary shadow-sm fw-semibold" onClick={handleModalClose}>
                                        Abort & Close
                                    </button>
                                </>
                            )}

                            {phase === 'completed' && (
                                <button className="btn btn-primary px-5 shadow-sm fw-bold" onClick={handleModalClose}>
                                    Done
                                </button>
                            )}
                        </div>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
